import { Component, OnInit, OnDestroy, output, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { selectAuthError } from '../../../store/auth/auth.selectors';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { CustomInputComponent } from '../../inputs/custom-input/custom-input.component';
import { CustomButtonComponent } from '../../inputs/custom-button/custom-button.component';
import { RouterModule } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { loginSuccess, loginFailure } from '../../../store/auth/auth.actions';
import { TranslateModule } from '@ngx-translate/core';
import { CustomFormValidators } from '../../../helperts/custom-form-validators.helper';

@Component({
  selector: 'lib-signin',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    CustomInputComponent,
    CustomButtonComponent,
  ],
  templateUrl: './signin.component.html',
})
export class SignInComponent implements OnInit, OnDestroy {
  forgotPasswordLink = input<string>('/forgot-password');
  registerLink = input<string>('/register');
  featureImageUrl = input<string | null>('public/shared/images/background-login.jpg');
  companyLogoUrl = input<string>('public/shared/images/logos/logotipo.svg');
  /**
   * Tailwind CSS background utility class to set the tone/color of the image container
   * e.g. 'bg-gray-50', 'bg-slate-900', etc.
   */
  imageContainerBgTone = input<string>('bg-blue-950');
  signInSubmit = output<{ email: string; password: string }>();
  showRegisterLink = input<boolean>(true);
  signInForm!: FormGroup;
  errorMessage: string | null = null;
  /** Loading spinner flag */
  loading = signal(false);

  private _destroy$ = new Subject<void>();

  constructor(
    private _fb: FormBuilder,
    private _store: Store,
    private _actions$: Actions
  ) {
    // Turn off loading when login succeeds or fails
    this._actions$
      .pipe(ofType(loginSuccess, loginFailure), takeUntil(this._destroy$))
      .subscribe(() => this.loading.set(false));
  }

  ngOnInit(): void {
    this.signInForm = this._fb.group({
      email: new FormControl('', [Validators.required, CustomFormValidators.emailWithTld()]),
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
    });

    this._store
      .select(selectAuthError)
      .pipe(takeUntil(this._destroy$))
      .subscribe((error) => {
        this.errorMessage = error;
      });
  }

  onSubmit(): void {
    if (this.signInForm.valid) {
      // Clear previous error messages on new submission attempt
      this.errorMessage = null;
      this.loading.set(true);
      this.signInSubmit.emit(this.signInForm.value);
      // Aquí iría la lógica de autenticación
    } else {
      this.signInForm.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get email() {
    return this.signInForm.get('email') as FormControl;
  }

  get password() {
    return this.signInForm.get('password') as FormControl;
  }
}
